import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Immunization } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export type ImmunizationDetailProps = {
  immunization: WithId<Immunization>;
  onBack?: () => void;
};

export const ImmunizationDetail = ({ immunization, onBack }: ImmunizationDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');

  const name = formatCodeableConcept(immunization.vaccineCode) || t('unknownImmunization');

  const items = [
    {
      title: t('dateAdministered'),
      value: immunization.occurrenceDateTime
        ? format(parseISO(immunization.occurrenceDateTime), DATE_FORMATS.DATE)
        : t('unknown'),
    },
    { title: t('lotNumber'), value: immunization.lotNumber ?? t('unknown') },
    { title: t('manufacturer'), value: immunization.manufacturer?.display ?? t('unknown') },
    {
      title: t('site'),
      value: immunization.site ? formatCodeableConcept(immunization.site) : t('unknown'),
    },
    {
      title: t('route'),
      value: immunization.route ? formatCodeableConcept(immunization.route) : t('unknown'),
    },
    {
      title: t('doseQuantity'),
      value:
        immunization.doseQuantity?.value !== undefined && immunization.doseQuantity?.value !== null
          ? `${immunization.doseQuantity.value}${immunization.doseQuantity.unit ? ` ${immunization.doseQuantity.unit}` : ''}`
          : t('unknown'),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {onBack && (
        <Button
          id="back-to-immunizations-button"
          variant="ghost"
          onClick={onBack}
          className="mb-3 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToImmunizations')}</span>
        </Button>
      )}

      <div className="mb-3 flex items-center gap-2">
        <h1 className="text-sm font-medium">{name}</h1>
        <Badge variant="outline">{immunization.status ?? t('unknown')}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm md:grid-cols-2">
        {items.map((item) => (
          <div key={item.title}>
            <div className="text-xs text-muted-foreground">{item.title}</div>
            <div>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
